// src/encounter/services/use-answer-set-template.ts
// The Questionnaire an answer set was answered against: `GET /api/questionnaire-responses/{id}/template`. 200 → the
// template as it was when the set was answered; 404 → not available (retired, or not readable by this tenant). A
// template does not change under a stored row, so it is read once.
import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { FhirQuestionnaire } from '@/types/template-studio-api';

export const answerSetTemplateKey = (rowId: string | null) => ['answer-set-template', rowId] as const;

/** The query for one row, for a caller that reads several at once (`useQueries`). `null` data = the server has no template for it (404). */
export function answerSetTemplateOptions(rowId: string | null, enabled = true) {
  return queryOptions<FhirQuestionnaire | null>({
    queryKey: answerSetTemplateKey(rowId),
    enabled: enabled && !!rowId,
    staleTime: Infinity,
    // A 404 is an answer, not a transient failure: never retried.
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
    queryFn: async ({ signal }) => {
      try {
        return await get<FhirQuestionnaire>(`/api/questionnaire-responses/${encodeURIComponent(rowId as string)}/template`, { signal });
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

export function useAnswerSetTemplate(rowId: string | null, enabled = true) {
  return useQuery(answerSetTemplateOptions(rowId, enabled));
}
